export type TradeMarket = 'Commodity' | 'Crypto' | 'Forex' | 'Stock'

export type LogoDevLookup =
  | { mode: 'ticker'; identifier: string }
  | { mode: 'crypto'; identifier: string }

export type LogoDevImageOptions = {
  size?: number
  format?: 'jpg' | 'png' | 'webp'
  theme?: 'light' | 'dark'
  retina?: boolean
}

const US_EXCHANGES = new Set([
  'NASDAQ',
  'NYSE',
  'AMEX',
  'BATS',
  'ARCA',
  'NYSEARCA',
  'NYSE ARCA',
  'NASDAQ NMS',
  'NASDAQ GM',
  'NASDAQ GS',
  'NASDAQ CAPITAL MARKET',
  'NEW YORK STOCK EXCHANGE',
  'NYSE AMERICAN',
  'XNAS',
  'XNYS',
  'XASE',
  'ARCX',
])

const EXCHANGE_SUFFIX: Record<string, string> = {
  BIST: '.IS',
  IST: '.IS',
  XIST: '.IS',
  LSE: '.L',
  XLON: '.L',
  LONDON: '.L',
  XETRA: '.DE',
  XETR: '.DE',
  TSE: '.T',
  XTKS: '.T',
  TOKYO: '.T',
  HKEX: '.HK',
  XHKG: '.HK',
  ASX: '.AX',
  XASX: '.AX',
  TSX: '.TO',
  XTSE: '.TO',
  EPA: '.PA',
  XPAR: '.PA',
  PAR: '.PA',
  PARIS: '.PA',
  AMS: '.AS',
  XAMS: '.AS',
  SWX: '.SW',
  XSWX: '.SW',
  STO: '.ST',
  XSTO: '.ST',
  KRX: '.KS',
  XKRX: '.KS',
  TWSE: '.TW',
  XTAI: '.TW',
  NSE: '.NS',
  XNSE: '.NS',
  BSE: '.BO',
  XBOM: '.BO',
}

const CRYPTO_QUOTE_SUFFIXES = ['USDT', 'USDC', 'USD', 'EUR', 'GBP', 'BTC', 'ETH', 'BNB'] as const

export function getLogoDevPublishableKey(): string {
  return (import.meta.env.VITE_LOGO_DEV_PUBLISHABLE_KEY || '').trim()
}

export function isLogoDevConfigured(): boolean {
  return getLogoDevPublishableKey().length > 0
}

function logoDevSuffixForExchange(exchange?: string | null, micCode?: string | null): string {
  const candidates = [micCode, exchange]
    .filter((value): value is string => Boolean(value?.trim()))
    .map((value) => value.trim().toUpperCase())
  for (const key of candidates) {
    if (US_EXCHANGES.has(key) || key.includes('NASDAQ') || key.includes('NEW YORK')) return ''
    if (EXCHANGE_SUFFIX[key]) return EXCHANGE_SUFFIX[key]
  }
  return ''
}

function normalizeBaseSymbol(symbol: string): string {
  return symbol.trim().toUpperCase()
}

function stripInstrumentSuffixes(symbol: string): string {
  return symbol.replace(/=F$/i, '').replace(/=X$/i, '')
}

export function cryptoLogoIdentifier(symbol: string): string {
  const raw = normalizeBaseSymbol(symbol)
  if (!raw) return raw
  if (raw.includes('-')) return raw.split('-')[0]
  if (raw.includes('/')) return raw.split('/')[0]
  for (const quote of CRYPTO_QUOTE_SUFFIXES) {
    if (raw.endsWith(quote) && raw.length > quote.length) {
      return raw.slice(0, -quote.length)
    }
  }
  return raw
}

export function stockTickerIdentifier(
  symbol: string,
  options?: { exchange?: string | null; micCode?: string | null }
): string {
  let raw = stripInstrumentSuffixes(symbol.trim())
  if (!raw) return raw
  if (/\.[A-Za-z]{1,6}$/.test(raw)) {
    return raw.toUpperCase()
  }
  if (/-[A-Za-z]{1,6}$/.test(raw)) {
    const lastDash = raw.lastIndexOf('-')
    return `${raw.slice(0, lastDash)}.${raw.slice(lastDash + 1)}`.toUpperCase()
  }
  const suffix = logoDevSuffixForExchange(options?.exchange, options?.micCode)
  const base = normalizeBaseSymbol(raw)
  return suffix ? `${base}${suffix}` : base
}

export function resolveLogoDevLookup(
  symbol: string,
  market: TradeMarket | string,
  options?: { exchange?: string | null; micCode?: string | null }
): LogoDevLookup | null {
  const trimmed = symbol?.trim()
  if (!trimmed) return null

  if (market === 'Crypto') {
    const identifier = cryptoLogoIdentifier(trimmed)
    return identifier ? { mode: 'crypto', identifier } : null
  }

  if (market === 'Forex') {
    return null
  }

  const identifier = stockTickerIdentifier(trimmed, options)
  return identifier ? { mode: 'ticker', identifier } : null
}

export function buildLogoDevCdnPath(
  lookup: LogoDevLookup,
  options?: LogoDevImageOptions
): string {
  const params = new URLSearchParams()
  const token = getLogoDevPublishableKey()
  if (token) params.set('token', token)
  if (options?.size) params.set('size', String(options.size))
  if (options?.format) params.set('format', options.format)
  if (options?.theme) params.set('theme', options.theme)
  if (options?.retina) params.set('retina', 'true')
  params.set('fallback', '404')

  const segment = lookup.mode === 'crypto'
    ? `crypto/${encodeURIComponent(lookup.identifier)}`
    : `ticker/${encodeURIComponent(lookup.identifier)}`

  const query = params.toString()
  return `https://img.logo.dev/${segment}${query ? `?${query}` : ''}`
}

export function buildLogoDevCdnUrl(
  symbol: string,
  market: TradeMarket | string,
  options?: { exchange?: string | null; micCode?: string | null } & LogoDevImageOptions
): string | null {
  if (!isLogoDevConfigured()) return null
  const lookup = resolveLogoDevLookup(symbol, market, options)
  if (!lookup) return null
  const { exchange, micCode, ...imageOptions } = options || {}
  return buildLogoDevCdnPath(lookup, imageOptions)
}

export function symbolMonogram(symbol: string, length = 2): string {
  const raw = symbol.trim()
  if (!raw) return '—'
  return raw.slice(0, length).toUpperCase()
}
